import update from 'immutability-helper'
import { Game, Sheet, GameMetadata, GameBoard, GameOptions, ConnectionStatus, PlanetDeeds } from '@prometheus/types'
import { Patch, applyPatches, enablePatches } from 'immer'

enablePatches()

export interface Message {
  id?: number
  type: 'error' | 'info'
  message: string
}

interface GameState {
  games: Game[]
  game?: Game
  sheet?: Sheet
  admin_sheets?: Sheet[]
  metadata?: GameMetadata
  options?: GameOptions
  game_board?: GameBoard
  planets?: PlanetDeeds
  errors: Message[]
  fullscreen: boolean
  galaxy_display: boolean
  sort_owned?: string
  sort_choice?: string
  connection_status: ConnectionStatus
}

const initialState: GameState = {
  games: [],
  errors: [],
  fullscreen: false,
  galaxy_display: false,
  connection_status: {},
}

const SET_GAMES = 'games/SET_GAMES'
const SET_GAME = 'games/SET_GAME'
const SET_SHEET = 'games/SET_SHEET'
const SET_SHEET_DELTA = 'games/SET_SHEET_DELTA'
const SET_SHEET_PATCHES = 'games/SET_SHEET_PATCHES'
const SET_METADATA = 'games/SET_METADATA'
const SET_METADATA_DELTA = 'games/SET_METADATA_DELTA'
const SET_METADATA_PATCHES = 'games/SET_METADATA_PATCHES'
const SET_OPTIONS = 'games/SET_OPTIONS'
const SET_OPTIONS_PATCHES = 'games/SET_OPTIONS_PATCHES'
const SET_GAME_BOARD = 'games/SET_GAME_BOARD'
const SET_BOARD_PATCHES = 'games/SET_BOARD_PATCHES'
const SET_PLANETS_PATCHES = 'games/SET_PLANETS_PATCHES'
const SET_FULLSCREEN = 'games/SET_FULLSCREEN'
const SET_MAP = 'games/SET_MAP'
const SET_ADMIN_SHEETS = 'games/SET_ADMIN_SHEETS'
const SET_CONNECTION_STATUS_DELTA = 'games/SET_CONNECTION_STATUS_DELTA'

const SET_SORT_OWNED = 'games/SORT_OWNED'
const SET_SORT_CHOICE = 'games/SET_SORT_CHOICE'

const PUSH_ERROR = 'games/PUSH_ERROR'
const PULL_ERROR = 'games/PULL_ERROR'

const CLEAR_MESSAGES = 'games/CLEAR_MESSAGES'

const RESET_STATE = 'games/RESET_STATE'

export default function (state: GameState = initialState, action: GameActionTypes): GameState {
  switch (action.type) {
  case SET_CONNECTION_STATUS_DELTA:
    return update(state, { connection_status: action.payload })
  case SET_GAMES:
    return { ...state, games: action.payload }
  case SET_GAME:
    return { ...state, game: action.payload }
  case SET_SHEET:
    return { ...state, sheet: action.payload }
  case SET_SHEET_DELTA:
    return { ...state, sheet: update(state.sheet, action.payload) }
  case SET_SHEET_PATCHES:
    return applyPatches(state, action.payload)
  case SET_METADATA:
    return { ...state, metadata: action.payload }
  case SET_METADATA_DELTA:
    return { ...state, metadata: update(state.metadata, action.payload) }
  case SET_METADATA_PATCHES:
    return applyPatches(state, action.payload)
  case SET_OPTIONS:
    return { ...state, options: action.payload }
  case SET_OPTIONS_PATCHES:
    return applyPatches(state, action.payload)
  case SET_GAME_BOARD:
    return { ...state, game_board: action.payload }
  case SET_BOARD_PATCHES:
    return applyPatches(state, action.payload)
  case SET_PLANETS_PATCHES:
    return applyPatches(state, action.payload)
  case SET_FULLSCREEN:
    return { ...state, fullscreen: action.payload }
  case SET_MAP:
    return { ...state, galaxy_display: action.payload }
  case SET_ADMIN_SHEETS:
    return { ...state, admin_sheets: action.payload }
  case SET_SORT_OWNED:
    return { ...state, sort_owned: action.payload }
  case SET_SORT_CHOICE:
    return { ...state, sort_choice: action.payload }
  case PUSH_ERROR:
    return { ...state, errors: [...state.errors, action.payload] }
  case PULL_ERROR:
    const index = state.errors.indexOf(action.payload)
    return update(state, {
      errors: { $splice: [[index, 1]] },
    })
  case CLEAR_MESSAGES:
    return update(state, {
      errors: { $set: [] },
    })
  case RESET_STATE:
    return update(state, {
      $unset: ['game', 'sheet', 'game_board', 'metadata', 'options', 'planets'],
      errors: { $set: [] },
    })
  default:
    return state
  }
}

interface ClearMessagesAction {
  type: typeof CLEAR_MESSAGES
}

export function clearMessages(): ClearMessagesAction {
  return {
    type: CLEAR_MESSAGES
  }
}

interface SetConnectionStatusDelta {
  type: typeof SET_CONNECTION_STATUS_DELTA
  payload: any
}

export function setConnectionStatusDelta(delta: any): SetConnectionStatusDelta {
  return {
    type: SET_CONNECTION_STATUS_DELTA,
    payload: delta,
  }
}

interface SetGamesAction {
  type: typeof SET_GAMES
  payload: Game[]
}

export function setGames(games: Game[]): SetGamesAction {
  return {
    type: SET_GAMES,
    payload: games,
  }
}

interface SetGameAction {
  type: typeof SET_GAME
  payload?: Game
}

export function setGame(game?: Game): SetGameAction {
  return {
    type: SET_GAME,
    payload: game,
  }
}

interface SetSheetAction {
  type: typeof SET_SHEET
  payload?: Sheet
}

export function setSheet(sheet?: Sheet): SetSheetAction {
  return {
    type: SET_SHEET,
    payload: sheet,
  }
}

interface SetSheetDeltaAction {
  type: typeof SET_SHEET_DELTA
  payload: any
}

export function setSheetDelta(sheetChanges: any): SetSheetDeltaAction {
  return {
    type: SET_SHEET_DELTA,
    payload: sheetChanges,
  }
}

interface SetSheetPatchesAction {
  type: typeof SET_SHEET_PATCHES
  payload: Patch[]
}

export function setSheetPatches(patches: Patch[]): SetSheetPatchesAction {
  return {
    type: SET_SHEET_PATCHES,
    payload: patches,
  }
}

interface SetAdminSheetsAction {
  type: typeof SET_ADMIN_SHEETS
  payload?: Sheet[]
}

export function setAdminSheets(sheets?: Sheet[]): SetAdminSheetsAction {
  return {
    type: SET_ADMIN_SHEETS,
    payload: sheets,
  }
}

interface SetMetadataAction {
  type: typeof SET_METADATA
  payload?: GameMetadata
}

export function setMetadata(metadata?: GameMetadata): SetMetadataAction {
  return {
    type: SET_METADATA,
    payload: metadata,
  }
}

interface SetMetadataDeltaAction {
  type: typeof SET_METADATA_DELTA
  payload?: any
}

export function setMetadataDelta(metadataChanges?: any): SetMetadataDeltaAction {
  return {
    type: SET_METADATA_DELTA,
    payload: metadataChanges,
  }
}

interface SetMetadataPatchesAction {
  type: typeof SET_METADATA_PATCHES
  payload: Patch[]
}

export function setMetadataPatches(patches: Patch[]): SetMetadataPatchesAction {
  return {
    type: SET_METADATA_PATCHES,
    payload: patches,
  }
}

interface SetOptionsAction {
  type: typeof SET_OPTIONS
  payload?: GameOptions
}

export function setOptions(options?: GameOptions): SetOptionsAction {
  return {
    type: SET_OPTIONS,
    payload: options,
  }
}

interface SetOptionsPatchesAction {
  type: typeof SET_OPTIONS_PATCHES
  payload: Patch[]
}

export function setOptionsPatches(patches: Patch[]): SetOptionsPatchesAction {
  return {
    type: SET_OPTIONS_PATCHES,
    payload: patches,
  }
}

interface SetGameBoardAction {
  type: typeof SET_GAME_BOARD
  payload?: GameBoard
}

export function setGameBoard(board?: GameBoard): SetGameBoardAction {
  return {
    type: SET_GAME_BOARD,
    payload: board,
  }
}

interface SetBoardPatchesAction {
  type: typeof SET_BOARD_PATCHES
  payload: Patch[]
}

export function setBoardPatches(patches: Patch[]): SetBoardPatchesAction {
  return {
    type: SET_BOARD_PATCHES,
    payload: patches,
  }
}

interface SetPlanetsPatchesAction {
  type: typeof SET_PLANETS_PATCHES
  payload: Patch[]
}

export function setPlanetsPatches(patches: Patch[]): SetPlanetsPatchesAction {
  return {
    type: SET_PLANETS_PATCHES,
    payload: patches,
  }
}

interface SetFullscreenAction {
  type: typeof SET_FULLSCREEN
  payload: boolean
}

export function setFullscreen(fullscreen: boolean): SetFullscreenAction {
  return {
    type: SET_FULLSCREEN,
    payload: fullscreen,
  }
}

interface SetMapAction {
  type: typeof SET_MAP
  payload: boolean
}

export function setMap(fullscreen: boolean): SetMapAction {
  return {
    type: SET_MAP,
    payload: fullscreen,
  }
}

interface SetSortOwnedAction {
  type: typeof SET_SORT_OWNED
  payload: string
}

export function setSortOwned(fullscreen: string): SetSortOwnedAction {
  return {
    type: SET_SORT_OWNED,
    payload: fullscreen,
  }
}

interface SetSortChoiceAction {
  type: typeof SET_SORT_CHOICE
  payload: string
}

export function setSortChoice(fullscreen: string): SetSortChoiceAction {
  return {
    type: SET_SORT_CHOICE,
    payload: fullscreen,
  }
}

interface PushErrorAction {
  type: typeof PUSH_ERROR
  payload: Message
}

export function pushError(message: Message): PushErrorAction {
  return {
    type: PUSH_ERROR,
    payload: {
      ...message,
      id: Date.now()
    },
  }
}

interface PullErrorAction {
  type: typeof PULL_ERROR
  payload: Message
}

export function pullError(message: Message): PullErrorAction {
  return {
    type: PULL_ERROR,
    payload: message,
  }
}

interface ResetStateAction {
  type: typeof RESET_STATE,
}

export function resetState(): ResetStateAction {
  return {
    type: RESET_STATE,
  }
}

export type GameActionTypes = SetGamesAction |
  SetGameAction |
  SetSheetAction |
  SetSheetDeltaAction |
  SetSheetPatchesAction |
  SetAdminSheetsAction |
  SetMetadataAction |
  SetMetadataDeltaAction |
  SetMetadataPatchesAction |
  SetOptionsAction |
  SetOptionsPatchesAction |
  PushErrorAction |
  PullErrorAction |
  SetGameBoardAction |
  SetBoardPatchesAction |
  SetPlanetsPatchesAction |
  SetFullscreenAction |
  SetMapAction |
  SetSortChoiceAction |
  SetSortOwnedAction |
  SetConnectionStatusDelta |
  ClearMessagesAction |
  ResetStateAction
